import { EVENT_MAX_TAGS, EVENT_TAG_MAX_LENGTH } from "../types/event";

/**
 * Pure helpers for event tags (the Tags combobox on the athlete event form).
 * Tag names are display strings; identity is case-insensitive.
 */

/** Trim, collapse inner whitespace and cap at EVENT_TAG_MAX_LENGTH. */
export const normaliseTagName = (
  raw: string | null | undefined,
  maxLength: number = EVENT_TAG_MAX_LENGTH,
): string =>
  (raw ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength)
    .trim();

/** Case-insensitive comparison key for a tag name. */
export const tagKey = (name: string): string =>
  normaliseTagName(name).toLowerCase();

/**
 * Normalise a list of tag names: normalise each, drop empties, de-duplicate
 * case-insensitively (first spelling wins) and cap at `max`.
 */
export const dedupeTags = (
  names: (string | null | undefined)[] | null | undefined,
  max: number = EVENT_MAX_TAGS,
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names || []) {
    const name = normaliseTagName(raw);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (out.length >= max) break;
  }
  return out;
};

/** Can `name` be added to `selected` (non-empty, not a duplicate, under the cap)? */
export const canAddTag = (
  selected: string[],
  name: string,
  max: number = EVENT_MAX_TAGS,
): boolean => {
  const normalised = normaliseTagName(name);
  if (!normalised) return false;
  if (selected.length >= max) return false;
  const key = normalised.toLowerCase();
  return !selected.some((s) => tagKey(s) === key);
};

export interface TagOptions {
  /** Suggestions still selectable, filtered by the query (suggestion order). */
  options: string[];
  /** `Create "<text>"` candidate, or null when the text matches an existing tag. */
  createName: string | null;
}

/**
 * Dropdown contents for the tag select: suggestions that are not already
 * selected and contain the query (case-insensitive), plus a create option when
 * the (normalised) query matches no suggestion or selected tag exactly.
 */
export const filterTagOptions = (
  suggestions: string[],
  selected: string[],
  query: string,
): TagOptions => {
  const selectedKeys = new Set(selected.map(tagKey));
  const q = normaliseTagName(query).toLowerCase();
  const names = dedupeTags(suggestions, Number.MAX_SAFE_INTEGER);
  const options = names.filter((name) => {
    const key = name.toLowerCase();
    if (selectedKeys.has(key)) return false;
    return !q || key.includes(q);
  });
  const exists =
    !q || selectedKeys.has(q) || names.some((n) => n.toLowerCase() === q);
  return { options, createName: exists ? null : normaliseTagName(query) };
};

/**
 * Tag suggestions for an athlete (there is no organisation library): the union
 * of the tags on the given events (own + coach-issued), de-duplicated
 * case-insensitively, first spelling wins, sorted alphabetically.
 */
export const collectTagSuggestions = (
  events: { tags?: (string | null | undefined)[] | null }[],
): string[] =>
  dedupeTags(
    events.flatMap((e) => e.tags ?? []),
    Number.MAX_SAFE_INTEGER,
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
